import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import {
  BlokkieButton,
  BlokkieTypography,
  blokkieTheme,
} from '@tikkie/blokkie';

import { useAnalytics } from '@/hooks/analytics';
import { useCampaignFields } from '@/hooks/campaign-fields';
import { useCampaignType } from '@/hooks/campaign-type';
import { useIsSingleQrFlow, useRouteName } from '@/hooks/route-name';
import { useTokenCampaign } from '@/hooks/token-campaign';

import tikkieLogo from '@/assets/tikkie-logo.svg';

import { CampaignLandingImage } from '@/components/campaign-landing-image/campaign-landing-image';
import { FullPageLoader } from '@/components/full-page-loader/full-page-loader';
import { InstructionModalButton } from '@/components/instruction-modal-button/instruction-modal-button';

export default function LandingRoute() {
  const { campaignType, campaignId, token } = useParams();
  const { t } = useTranslation(['translation', 'a11y']);
  const { trackEvent } = useAnalytics();
  const {
    isCampaignTypeValid,
    isTomraCampaign,
    isTokenCampaign,
    toAnalyticCampaignType,
  } = useCampaignType();

  const routeName = useRouteName();
  const navigate = useNavigate();

  const { campaign, isPending: isCampaignValidationPending } = useTokenCampaign(
    isTokenCampaign(campaignType) ? campaignId : undefined
  );
  const campaignFields = useCampaignFields(campaign);
  const isSingleQrFlow = useIsSingleQrFlow();
  const depositButtonText = isSingleQrFlow
    ? t('landing.depositButtonV2')
    : campaignFields.landingPrimaryButtonTitle;

  const instructionTitle = isTomraCampaign(campaignType)
    ? t('landing.helpModal.tomra.instructionTitle')
    : t('landing.helpModal.instructionTitle');
  const instructions = isTomraCampaign(campaignType)
    ? t('landing.helpModal.tomra.instructions', { returnObjects: true })
    : t('landing.helpModal.instructions', { returnObjects: true });
  const a11yInstructions = isTomraCampaign(campaignType)
    ? t('a11y:landing.helpModal.tomra.instructions', { returnObjects: true })
    : t('a11y:landing.helpModal.instructions', { returnObjects: true });

  useEffect(() => {
    if (!campaignId || !campaignType || (campaignType !== 'tomra' && !token)) {
      navigate('/404');
      return;
    }

    if (!isCampaignTypeValid(campaignType)) {
      navigate('/404');
      return;
    }

    trackEvent('web_recycling_start', {
      campaignId,
      campaignType: toAnalyticCampaignType(campaignType),
    });
  }, []);

  const handleDepositClick = () => {
    trackEvent('web_recycling_primaryButton', {
      page: 'web_recycling_start',
      campaignId,
      campaignType: toAnalyticCampaignType(campaignType),
    });

    navigate(routeName('/iban'));
  };

  if (isCampaignValidationPending) {
    return <FullPageLoader />;
  }

  return (
    <div
      style={{
        marginTop: '-4rem',
        textAlign: 'center',
        padding: blokkieTheme.spacing(2),
        overflow: 'hidden',
      }}
    >
      <CampaignLandingImage image={campaignFields.landingImage} />

      <BlokkieTypography
        variant="h1"
        tabIndex={0}
        style={{ position: 'relative', top: '-2rem' }}
        aria-label={campaignFields.landingTitleAriaLabel}
      >
        <span style={{ fontSize: '2rem', fontWeight: 600 }}>
          {campaignFields.landingTitle}
          <img
            src={tikkieLogo}
            alt={t('imgs.tikkieLogo')}
            aria-hidden="true"
            style={{
              paddingLeft: '1rem',
              height: '3rem',
              position: 'relative',
              top: '0.7rem',
            }}
          />
        </span>
      </BlokkieTypography>

      <div>
        <BlokkieButton
          id="deposit-button"
          variant="primary"
          style={{
            marginTop: blokkieTheme.spacing(1),
            width: '100%',
            maxWidth: blokkieTheme.breakpoints.mediumPhone.min,
          }}
          className="overflow-button"
          onClick={handleDepositClick}
          aria-label={depositButtonText}
        >
          <BlokkieTypography variant="buttonLarge">
            {depositButtonText}
          </BlokkieTypography>
        </BlokkieButton>
      </div>

      {!isSingleQrFlow && (
        <div>
          <InstructionModalButton
            instructionTitle={instructionTitle}
            openButtonText={t('landing.helpButton')}
            closeButtonText={t('landing.helpModal.closeButton')}
            instructions={instructions}
            a11yInstructions={a11yInstructions}
            onButtonClick={() => {
              trackEvent('web_recycling_secondaryButton', {
                page: 'web_recycling_start',
                campaignId,
                campaignType: toAnalyticCampaignType(campaignType),
              });

              trackEvent('web_recycling_explanation', {
                page: 'web_recycling_start',
                campaignId,
                campaignType: toAnalyticCampaignType(campaignType),
              });
            }}
          />
        </div>
      )}
    </div>
  );
}
